import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonSelect from "../../common/CommonSelect";
import { validateAddressField } from "./billingaddresshelper";

const stateMapping = {
  AK: "Alaska",
  AL: "Alabama",
  AR: "Arkansas",
  AS: "American Samoa",
  AZ: "Arizona",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DC: "District of Columbia",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  GU: "Guam",
  HI: "Hawaii",
  IA: "Iowa",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  MA: "Massachusetts",
  MD: "Maryland",
  ME: "Maine",
  MI: "Michigan",
  MN: "Minnesota",
  MO: "Missouri",
  MP: "Northern Mariana Islands",
  MS: "Mississippi",
  MT: "Montana",
  NC: "North Carolina",
  ND: "North Dakota",
  NE: "Nebraska",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NV: "Nevada",
  NY: "New York",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  PR: "Puerto Rico",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VA: "Virginia",
  VI: "Virgin Islands",
  VT: "Vermont",
  WA: "Washington",
  WI: "Wisconsin",
  WV: "West Virginia",
  WY: "Wyoming",
};

const fullStates = Object.values(stateMapping).sort((a, b) =>
  a.localeCompare(b),
);

const BillingAddress = ({
  address = {},
  onChange,
  onValidationChange,
  showErrors,
}) => {
  const anchorRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  React.useEffect(() => {
    // Initial and periodic validation
    const fields = ["street", "city", "state", "zip"];
    const newErrors = {};
    fields.forEach((field) => {
      const error = validateAddressField(field, address[field] || "");
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
  }, [address]);

  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getErrorProps = (field) => {
    const isInvalid = !!errors[field];
    const shouldShow = touched[field] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[field] : "",
    };
  };

  const handleOpenSelect = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSelect = () => {
    setAnchorEl(null);
  };

  const displayState =
    stateMapping[address.state] || address.state || "Select State";

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        Billing Address<span style={{ color: "#E53935" }}>*</span>
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
        >
          Street
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          autoComplete="new-password"
          placeholder="Enter street"
          value={address.street || ""}
          onChange={(e) => {
            if (e.target.value.length <= 60) onChange("street", e.target.value);
          }}
          onBlur={() => handleBlur("street")}
          {...getErrorProps("street")}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                      mr: 0.5,
                    }}
                  >
                    {(address.street || "").length}/60
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: getErrorProps("street").error
                  ? "#d32f2f"
                  : "#E8E8E9",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              py: 1.5,
              color: "#0e1324",
              pr: 0,
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
        >
          Line
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          autoComplete="new-password"
          placeholder="Enter line 2"
          value={address.line === "-" ? "" : address.line || ""}
          onChange={(e) => {
            if (e.target.value.length <= 60) onChange("line", e.target.value);
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                      mr: 0.5,
                    }}
                  >
                    {(address.line === "-" ? "" : address.line || "").length}/60
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": { borderColor: "#E8E8E9" },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              py: 1.5,
              color: "#0e1324",
              pr: 0,
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 5, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
            >
              City
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              autoComplete="new-password"
              placeholder="Enter city"
              value={address.city || ""}
              onChange={(e) => {
                if (e.target.value.length <= 50)
                  onChange("city", e.target.value);
              }}
              onBlur={() => handleBlur("city")}
              {...getErrorProps("city")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(address.city || "").length}/50
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: getErrorProps("city").error
                      ? "#d32f2f"
                      : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                  pr: 0,
                },
              }}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 4, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
            >
              State
            </Typography>
            <Box
              ref={anchorRef}
              onClick={handleOpenSelect}
              sx={{
                width: "100%",
                height: "45px",
                border: "1px solid",
                borderColor: getErrorProps("state").error
                  ? "#d32f2f"
                  : "#E8E8E9",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0 12px",
                backgroundColor: "#FFFFFF",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: address.state ? "#0e1324" : "#999893",
                }}
              >
                {displayState}
              </Typography>
              <KeyboardArrowDownIcon
                sx={{
                  color: getErrorProps("state").error ? "#d32f2f" : "#6B7280",
                  fontSize: "20px",
                  transition: "transform 0.2s",
                  transform: anchorEl ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Box>
            {getErrorProps("state").error && (
              <Typography
                sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0, ml: 1.75 }}
              >
                {getErrorProps("state").helperText}
              </Typography>
            )}
            <CommonSelect
              id="billing-state-select"
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={handleCloseSelect}
              noteTypes={fullStates}
              selectedTypes={[displayState]}
              handleTypeToggle={(type) => {
                const code = Object.keys(stateMapping).find(
                  (key) => stateMapping[key] === type,
                );
                onChange("state", code || type);
                handleCloseSelect();
              }}
              width={
                anchorRef.current?.offsetWidth
                  ? `${anchorRef.current.offsetWidth}px`
                  : "240px"
              }
            />
          </Box>
        </Box>
        <Box sx={{ flex: 3, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
            >
              Zip
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              autoComplete="new-password"
              placeholder="Enter zip code"
              value={address.zip || ""}
              onChange={(e) => {
                if (e.target.value.length <= 10)
                  onChange("zip", e.target.value);
              }}
              onBlur={() => handleBlur("zip")}
              {...getErrorProps("zip")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(address.zip || "").length}/10
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: getErrorProps("zip").error
                      ? "#d32f2f"
                      : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                  pr: 0,
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

BillingAddress.propTypes = {
  address: PropTypes.object,
  onChange: PropTypes.func,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default BillingAddress;
